import React, { useEffect, useRef, useMemo } from 'react';

import { EventDateTicketSelectionUnrestricted } from './event-date-ticket-selection-unrestricted';

import type { SelectedTicketInfo, SelectedTicketsOptions } from '../../../logic/models';
import { clearUnselectedTickets } from '../../../mvcapi/buy-ticket-api';
import { TicketPriceSingleSelection } from './ticket-price-single-selection';

import { useIFrameResize } from '../../../logic/hooks/utils/use-iframe-resize';
import { modal } from '@tsinternetsale/common';
import TM from 'ts-translate-js';
import { TicketSearchWizard } from './ticket-search-wizard';
import { ModalPopup } from '../../molecules';
import { useVenuePlanControl } from '../../../logic/hooks/use-venue-plan-control';
import { useAddTicketsToCartStore } from '../../../stores';
import { getTicketsFromItem } from '../../../stores/add-to-cart-stores/models/add-to-cart-items';
import { useAddPackageToCartStoreExt } from '../../../stores/add-to-cart-stores/add-package-to-cart-store-ext';

/*
TODO:
[x] La question de credit card conditions apparait à chaque fois
[x] 2 unselect tickets quand on décoche un siège dans le plan
[x] Remonter le load event date dans buy ticket one pager

*/

const tr = TM.boundTR;
const trN = tr;

type TicketSelectionUnrestrictedProps = {
    //packageId: number;
    //eventDateId: number;
    options?: SelectedTicketsOptions;
    //promoInfo?: PromoInfoShort;
    //hiddenPromo: boolean;
    //onPromoUpdated?: () => void;
}

// INFO: New sale

export function TicketSelectionUnrestricted({options}: TicketSelectionUnrestrictedProps) {
    const fetchingEventDate = useAddTicketsToCartStore(s => s.fetchingEventDate);
    const planRefreshId = useAddTicketsToCartStore(s => s.planRefreshId);
    const showTicketPriceSelection = useAddTicketsToCartStore(s => s.showTicketPriceSelection);
    const tempSelectedAddToCartItems = useAddTicketsToCartStore(s => s.tempSelectedAddToCartItems);
    const showTicketSearchWizard = useAddTicketsToCartStore(s => s.showTicketSearchWizard );
    const clearAllEventTickets = useAddTicketsToCartStore(s => s.clearAllTickets );
    const clearAllPackageTickets = useAddPackageToCartStoreExt(s => s.clearAllTickets );
    
    const ticketCount = useAddTicketsToCartStore(s => s.ticketCount);
    const selectedSection = useAddTicketsToCartStore(s => s.selectedSection);
    const isAPackageSale = useAddPackageToCartStoreExt(s => s.isAPackageSale);
    const [ cancelRemoveTicketsProcess, showRemoveTicketsConfirmPopup, ticketsAwaitingForRemoval ] = useAddTicketsToCartStore(s => [ s.cancelRemoveTicketsProcess, s.showRemoveTicketsConfirmPopup, s.ticketsAwaitingForRemoval ]);
    const confirmRemoveTicketsProcess = useAddTicketsToCartStore(s => s.confirmRemoveTicketsProcess);
    const confirmRemovePackageTicketsProcess = useAddPackageToCartStoreExt(s => s.confirmRemoveTicketsProcess);
    const newPlanFocusPoint = useAddTicketsToCartStore(s => s.newPlanFocusPoint);
    const planControl = useVenuePlanControl();
    const modalContainer: React.RefObject<HTMLDivElement> = useRef();
    
    useIFrameResize();

    // Before we begin, clear user's previous unused selection.
    useEffect(() => { 
        clearUnselectedTickets(); 
        clearAllEventTickets(false);
        clearAllPackageTickets(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clean tickets if the user leaves the page
    useEffect(() => { // Component Load
        window.addEventListener("beforeunload", clearUnselectedTickets, true);
    
        return () => { // Component Unload
            window.removeEventListener("beforeunload", clearUnselectedTickets);
            clearUnselectedTickets();
        };
    }, []);


    const priceGroupSelectionModalTitle = useMemo(() => {
        if(ticketCount > 1) {
            const title = isAPackageSale() ? tr("forfaits") : tr("billets");
            if (selectedSection != null)
                return ticketCount + " " + title + " - " + selectedSection.Name;
            return ticketCount + " " + title;
        }
        else if (ticketCount === 1 || selectedSection != null) {
            // TODO: VOir ce qu'on va afficher si c'est un forfait pour le moment on affiche le nom du billet comme si je n'avais pas encore de forfait
            if(selectedSection != null)
                return selectedSection.Name;
            else {
                const tickets: SelectedTicketInfo[] = getTicketsFromItem(tempSelectedAddToCartItems[0]);
                return tickets[0].ticket.Name;
            }
        } else if (showTicketSearchWizard) {
            return isAPackageSale() ? tr("Sélection de forfaits") : tr("Sélection de billets");
        }
        return tr("Chargement...");
    }, [ticketCount, selectedSection, showTicketSearchWizard, isAPackageSale, tempSelectedAddToCartItems]);

    
    useEffect(() => {
        if(newPlanFocusPoint && planControl) {
            planControl.showPlanRequest(newPlanFocusPoint);
            // TODO: Do I need to clear this focus point to prevent weird behavior afterward?
        }
    }, [newPlanFocusPoint, planControl]);

    useEffect(() => {
        if(planRefreshId > 0 && planControl) {
            planControl.refreshSeatsOnPlan();
            // TODO: Do I need to clear this focus point to prevent weird behavior afterward?
        }
    }, [planRefreshId, planControl]);

    const title = tr("cartcontent.confirmtitle");

    let removeConfirmPopup = null;
    if(isAPackageSale()) 
        removeConfirmPopup = showRemoveTicketsConfirmPopup && <modal.ConfirmYesNo 
            container={modalContainer.current}
            showCloseButton={false}
            title={title}
            messagehtml={trN("Voulez-vous vraiment retirer ce forfait?", "Voulez-vous vraiment retirer ces forfaits?", {n: ticketsAwaitingForRemoval.ticketIds.length})}
            onNo={cancelRemoveTicketsProcess}
            onYes={confirmRemovePackageTicketsProcess}
            onClose={cancelRemoveTicketsProcess}
        />;
    else
        removeConfirmPopup = showRemoveTicketsConfirmPopup && <modal.ConfirmYesNo 
            container={modalContainer.current}
            showCloseButton={false}
            title={title}
            messagehtml={trN("Voulez-vous vraiment retirer ce billet?", "Voulez-vous vraiment retirer ces billets?", {n: ticketsAwaitingForRemoval.ticketIds.length})}
            onNo={cancelRemoveTicketsProcess}
            onYes={confirmRemoveTicketsProcess}
            onClose={cancelRemoveTicketsProcess}
        />;
    
    const packageId = null;
    if(packageId) {
        return <div>TODO: Package ticket selection</div>;
    } else {
        return <>
            <div ref={modalContainer} className="ticket-price-single-selection" />
            {
                (showTicketPriceSelection && tempSelectedAddToCartItems && tempSelectedAddToCartItems.length > 0) && <ModalPopup title={priceGroupSelectionModalTitle} container={modalContainer.current} showCloseButton={false} popupWidth={600}>
                    <TicketPriceSingleSelection /> 
                </ModalPopup>
            }
            <EventDateTicketSelectionUnrestricted options={options} />
            {showTicketSearchWizard && !fetchingEventDate &&
                <ModalPopup title={priceGroupSelectionModalTitle} container={modalContainer.current} showCloseButton={false} popupWidth={400} >
                    <TicketSearchWizard />
                </ModalPopup>
            }
            {removeConfirmPopup}
        </>;
    }
}

